import { useState } from 'react';

import { YUP_SCHEMA } from '@repo/constants';
import { RoundBtn } from '@repo/ui/Button';
import { EyeIcon, EyeIconVariant } from '@repo/ui/Icon';
import { InputFormikWithLimit } from '@repo/ui/TextField';
import { useFormik } from 'formik';
import { t } from 'i18next';
import styled from 'styled-components';
import * as Yup from 'yup';

import { postBubbletapLogin } from 'services/api/auth';
import { authenticateToBubbleTapServer } from 'utils/auth';
import { handleControlAuthError } from 'utils/error';

const LoginSchema = Yup.object({
  username: YUP_SCHEMA.USER_NAME,
  password: YUP_SCHEMA.PASSWORD,
});

export default function LoginForm() {
  const [showPassword, setShowPassword] = useState(false);
  const handlePasswordDisplay = () => setShowPassword((prev) => !prev);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      username: '',
      password: '',
    },
    initialTouched: { username: true },
    validationSchema: LoginSchema,
    onSubmit: async (values) => {
      try {
        const { username, password } = values;

        const { token: customToken } = await postBubbletapLogin({ username, password });
        await authenticateToBubbleTapServer(customToken);
      } catch (error: any) {
        // setLoading(false);
        handleControlAuthError(error, formik);
      }
    },
  });
  return (
    <>
      <InputFormikWithLimit
        formik={formik}
        name="username"
        label=""
        placeholder={t('general.username')}
        style={{ width: '100%' }}
        inputStyle={{ fontSize: '16px' }}
        enterSubmit={formik.handleSubmit}
        isUsernameOrPassword={true}
      />
      <SValidationSentence $isFormikError={Boolean(formik.touched.username) && Boolean(formik.errors.username)}>
        <li>{t(formik.errors.username ?? 'register.userinfo_username_format_validation_message')}</li>
      </SValidationSentence>
      <InputFormikWithLimit
        className="passwordInput"
        formik={formik}
        name="password"
        label=""
        placeholder={t('register.userinfo_password_placeholder')}
        type={showPassword ? 'text' : 'password'}
        style={{ marginTop: '22px', maxWidth: 400, width: '100%' }}
        inputStyle={{ fontSize: '16px' }}
        slot={
          <SIconContainer onClick={handlePasswordDisplay}>
            {showPassword ? <EyeIcon /> : <EyeIcon variant={EyeIconVariant.ON} />}
          </SIconContainer>
        }
        enterSubmit={formik.handleSubmit}
        isUsernameOrPassword={true}
      />
      <SValidationSentence $isFormikError={Boolean(formik.touched.password) && Boolean(formik.errors.password)}>
        <li>{t(formik.errors.password ?? 'setting.edit_password_change_your_password_validation_text')}</li>
      </SValidationSentence>
      <SLoginButton
        design={!formik.dirty || !formik.isValid ? 'inactive' : 'filled'}
        disabled={!formik.dirty || !formik.isValid}
        onClick={formik.handleSubmit}
        style={{ marginTop: '40px' }}
        radius="8px"
      >
        {t('login.userinfo_login')}
      </SLoginButton>
    </>
  );
}

const SIconContainer = styled.button`
  display: flex;
  align-items: center;

  & > svg {
    margin-bottom: 5px;
    width: 20px;
    height: 20px;
  }
`;
type SValidationSentenceProps = {
  $isFormikError: boolean;
};
const SValidationSentence = styled.ul<SValidationSentenceProps>`
  margin-top: 10px;
  text-align: left;

  li {
    font-size: 13px;
    color: ${({ theme, $isFormikError }) => ($isFormikError ? theme.palette.red : theme.palette.neutral[30])};
  }
`;

const SLoginButton = styled(RoundBtn)`
  margin-top: 60px;
  width: 100%;
  height: 48px;
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.palette.common.white};
`;
